import React, { useEffect, useRef, useState } from 'react';
import { OPEN_PROBLEM_REPORTER_EVENT } from '@/components/writing/StrugglingWriterNotice';
import { domToBlob } from 'modern-screenshot';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '@/config/firebase';
import { useAuthContext } from '@/contexts/AuthContext';
import { getConsoleLog } from '@/utils/consoleCapture';
import { submitProblemReport } from '@/core/apiProxy';
import Button from '@/components/ui/Button';
import Icon from '@/components/ui/Icon';
import { t } from '../i18n/t';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENTS, attachmentContentType, checkAttachments, type RefusedFile } from '@/utils/attachmentRules';

// Round 129b (R-A1): images, PDF and plain text only, under 10 MB, checked before upload (utils/attachmentRules.ts)
/** Round 124 - how long the screenshot may take before the report goes ahead without one */
const SCREENSHOT_TIMEOUT_MS = 8000;

/**
 * Round 124 - crypto.randomUUID() is missing in older Android WebViews / Safari < 15.4 and outside secure contexts; there it THREW inside
 * handleConfirm and the report was never sent. Fall back to getRandomValues, then to Math.random (an id only has to be unique, not secret).
 */
function newReportId(): string {
  const c = (typeof crypto !== 'undefined' ? crypto : undefined) as (Crypto & { randomUUID?: () => string }) | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

type Stage = 'idle' | 'form' | 'confirming' | 'submitting';

/**
 * Round 57, B — the report-a-bug widget. Present on every learner screen
 * (mounted in App.tsx, gated to user != null && !pathname.startsWith('/admin')
 * — owner ruling, Round 57 A: admins have other channels, pre-auth pages have
 * no identity to attach).
 *
 * Flow: floating button -> a small corner form (not a full-page overlay, so
 * the page underneath stays visible and is what the screenshot below
 * actually shows) -> on "Send report," the screenshot and console log are
 * captured immediately (before anything else changes on screen) -> the
 * permission dialog shows exactly what was captured and asks to confirm ->
 * only on confirm do the uploads and the API call actually happen.
 */
export default function ProblemReporter() {
  const { user, profile } = useAuthContext();
  const rootRef = useRef<HTMLDivElement>(null);

  const [stage, setStage] = useState<Stage>('idle');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [refusedFiles, setRefusedFiles] = useState<RefusedFile[]>([]);
  const [captured, setCaptured] = useState<{ blob: Blob; url: string } | null>(null);
  const [error, setError] = useState('');
  // Round 132b - after Send the learner sees a clear confirmation that says where the answer will come (Messages), not a 4-second toast
  const [sent, setSent] = useState<null | 'sent' | 'partial'>(null);
  // Round 137 (decision 9): "Contact support" under a struggling writer's feedback opens this same dialog
  useEffect(() => {
    const open = () => setStage((s) => (s === 'idle' ? 'form' : s));
    window.addEventListener(OPEN_PROBLEM_REPORTER_EVENT, open);
    return () => window.removeEventListener(OPEN_PROBLEM_REPORTER_EVENT, open);
  }, []);

  function reset() {
    if (captured) URL.revokeObjectURL(captured.url);
    setStage('idle');
    setDescription('');
    setAttachments([]);
    setAttachmentError('');
    setRefusedFiles([]);
    setCaptured(null);
    setError('');
  }

  function handleAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    const { accepted, refused } = checkAttachments(picked);
    setRefusedFiles(refused);
    // say at once what was not attached and why (a Word file, a file over 10 MB, a sixth file); the allowed ones are kept
    setAttachmentError(refused.length ? t('common:problemReporter.attachmentRules') : '');
    setAttachments(accepted.map(i => picked[i]));
    if (refused.length) e.target.value = '';   // let the learner pick again
  }

  async function handleSend() {
    if (!description.trim() || !user) return;
    setError('');
    // Captured NOW, before the permission dialog (or anything else)
    // covers the page — this is what "screenshot of this screen" means.
    //
    // Round 124 — a screenshot is a nice-to-have, never a precondition. Before this round a capture failure (a browser/canvas error, or
    // domToBlob simply hanging on a cross-origin resource) set `error` while the stage stayed 'form', and the form never rendered `error`:
    // the learner pressed "Send report" and NOTHING visibly happened, so no report was ever filed. Now the capture is time-boxed, and if it
    // fails the report goes ahead without a screenshot, saying so in the confirmation dialog.
    let blob: Blob | null = null;
    try {
      blob = await Promise.race([
        domToBlob(document.body, { filter: (node) => node !== rootRef.current && !rootRef.current?.contains(node) }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), SCREENSHOT_TIMEOUT_MS)),
      ]);
    } catch (err) {
      // never shown raw to the learner (a canvas SecurityError message is untranslated English); kept in the console log the report carries
      console.warn('[ProblemReporter] screenshot capture failed', err instanceof Error ? err.name : typeof err);
      blob = null;
    }
    setCaptured(blob ? { blob, url: URL.createObjectURL(blob) } : null);
    setStage('confirming');
  }

  /** Storage upload that reports failure instead of throwing: the report itself must still be filed. */
  async function tryUpload(path: string, data: Blob | File): Promise<boolean> {
    try {
      // the content type is set explicitly (storage.rules checks it) - inferred from the extension when the browser does not know it
      const contentType = 'name' in data ? attachmentContentType(data as File) ?? undefined : undefined;
      await uploadBytes(storageRef(storage, path), data, contentType ? { contentType } : undefined);
      return true;
    } catch (err) {
      console.warn('[ProblemReporter] attachment upload failed', (err as { code?: string })?.code ?? 'unknown');
      return false;
    }
  }

  async function handleConfirm() {
    if (!user) return;
    setStage('submitting');
    setError('');
    try {
      const reportId = newReportId();
      const basePath = `problemReports/${user.uid}/${reportId}`;

      // Round 124 — the REPORT is the source of truth (it is what lands in the admin Tickets list); the screenshot and files are best-effort
      // extras. A Storage failure (rules, network, size) used to abort everything before the API call; now it only drops that file.
      let failedUploads = 0;
      let screenshotPath: string | null = null;
      if (captured) {
        if (await tryUpload(`${basePath}/screenshot.png`, captured.blob)) screenshotPath = `${basePath}/screenshot.png`;
        else failedUploads++;
      }
      const attachmentPaths: string[] = [];
      const uploadRefused: RefusedFile[] = [];
      for (let i = 0; i < attachments.length; i++) {
        const file = attachments[i];
        const path = `${basePath}/${i}-${file.name}`;
        if (await tryUpload(path, file)) attachmentPaths.push(path);
        else { failedUploads++; uploadRefused.push({ name: file.name, reason: 'upload_failed' }); }
      }

      await submitProblemReport({
        reportId,
        description: description.trim(),
        url: window.location.href,
        screenshotPath,
        attachmentPaths,
        refusedAttachments: [...refusedFiles, ...uploadRefused],
        consoleLog: getConsoleLog(),
      });

      reset();
      setSent(failedUploads > 0 ? 'partial' : 'sent');
    } catch (err) {
      // `err` is submitProblemReport's ApiFetchError (the backend's `.message`, always English) — never shown to a learner directly. The
      // learner gets a translated sentence that fits the cause (signed out / throttled / generic) and can press Send again: the same
      // dialog stays open with their description and screenshot intact.
      const status = (err as { status?: number })?.status;
      setStage('confirming');
      setError(
        status === 401 ? t('common:problemReporter.signInAgain')
          : status === 429 ? t('common:problemReporter.tooManyRequests')
          : t('common:problemReporter.couldNotSendThisReport'),
      );
    }
  }

  function removeScreenshot() {
    if (captured) URL.revokeObjectURL(captured.url);
    setCaptured(null);
  }

  const displayName = profile?.preferredName || profile?.firstName || null;

  return (
    <>
      {/* Phase G, G4/G5 — `end-4` (not `right-4`): the bottom-right-anchored
          corner this component was flagged for by the RTL audit. `items-end`
          does NOT need to change — flexbox's `align-items: flex-end` is
          already writing-direction-aware per the flexbox spec (unlike
          absolute `left`/`right` positioning), so the popout form stays
          correctly aligned to the same corner as the button in RTL with no
          extra class. Verified live in a real RTL browser pass (G11). */}
      <div ref={rootRef} className="problem-reporter-fab fixed end-4 z-40 flex flex-col items-end gap-2">
        {stage === 'form' && (
          <div
            role="dialog"
            aria-label={t('common:problemReporter.reportAProblem')}
            className="w-[calc(100vw-2rem)] max-w-sm bg-card border border-line rounded-2xl shadow-float p-4 flex flex-col gap-3"
          >
            <p className="text-sm font-semibold text-ink">{t('common:problemReporter.reportAProblem')}</p>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('common:problemReporter.whatHappened')}
              rows={4}
              className="w-full border border-line rounded-xl px-3 py-2 text-sm bg-paper text-ink
                         focus:outline-none focus:ring-2 focus:ring-focus resize-none placeholder:text-muted"
            />
            <label className="text-xs text-muted">{t('common:problemReporter.attachFilesOptionalUpTo')}{MAX_ATTACHMENTS})
              <input
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={handleAttachmentChange}
                className="block w-full text-xs mt-1"
              />
            </label>
            {attachmentError && (
              <div role="alert" className="text-xs text-red-600" data-testid="attachment-error">
                <p>{attachmentError}</p>
                {refusedFiles.length > 0 && <ul className="list-disc ms-4">{refusedFiles.map(f => <li key={f.name} dir="auto">{f.name}</li>)}</ul>}
              </div>
            )}
            {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
            {/* Round D — flex-auto/min-w-fit instead of fullWidth on both
                (hard 50/50 split that wrapped longer Arabic labels inside
                their own half); see DeleteAccountModal.tsx's same fix for
                the full reasoning. */}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" className="flex-auto min-w-fit whitespace-nowrap" onClick={reset}>{t('common:actions.cancel')}</Button>
              <Button size="sm" className="flex-auto min-w-fit whitespace-nowrap" disabled={!description.trim()} onClick={handleSend}>{t('common:problemReporter.sendReport')}</Button>
            </div>
          </div>
        )}

        <button
          onClick={() => setStage(stage === 'idle' ? 'form' : 'idle')}
          aria-label={t('common:problemReporter.reportAProblem')}
          className="w-12 h-12 rounded-full bg-card border border-line shadow-float
                     flex items-center justify-center hover:bg-paper-2 transition-colors"
        >
          <Icon name="warning" size="md" />
        </button>
      </div>

      {(stage === 'confirming' || stage === 'submitting') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
          <div
            className="relative bg-card rounded-3xl shadow-2xl max-w-md w-full max-h-[90dvh] flex flex-col overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-label={t('common:problemReporter.beforeYouSendThis')}
          >
            {/* Round 132b - the body scrolls, the buttons below it never leave the screen (a full-size screenshot used to push Send off a phone) */}
            <div className="overflow-y-auto p-6 sm:p-8 pb-4" data-testid="report-confirm-body">
            <h2 className="text-xl font-bold text-ink mb-3">{t('common:problemReporter.beforeYouSendThis')}</h2>
            <p className="text-sm text-muted mb-2">{t('common:problemReporter.thisReportWillInclude')}</p>
            <ul className="text-sm text-muted list-disc ps-5 mb-4 space-y-1">
              {captured ? <li>{t('common:problemReporter.aScreenshotOfThisScreenNot')}</li> : <li>{t('common:problemReporter.noScreenshotThisTime')}</li>}
              <li>{t('common:problemReporter.yourRecentConsoleLogThisIs')}</li>
              {/* Phase G, G10 — was an inline `count !== 1 ? t('...s') : ''`
                  ternary gluing an English-only pluralization suffix onto a
                  base word (works only for languages that pluralize by
                  appending "s"; Arabic has 6 CLDR plural categories, not 2).
                  a t() call with a `count` var now lets i18next pick the right
                  `_one`/`_other` catalogue entry itself via Intl.PluralRules
                  — see catalog/en/common.json's
                  `problemReporter.attachedFileCount_one`/`_other`. */}
              {attachments.length > 0 && <li>{t('common:problemReporter.attachedFileCount', { count: attachments.length })}</li>}
              <li>{t('common:problemReporter.yourAccountEmail')}{displayName ? t('common:problemReporter.andNameDisplayname', { displayName }) : ''}{t('common:problemReporter.andThePageYouReOn')}</li>
            </ul>
            <p className="text-xs text-muted mb-4">{t('common:problemReporter.weStripAnythingThatLooksLike')}</p>
            {captured && (
              <div className="flex items-start gap-3 mb-2" data-testid="screenshot-thumb">
                <img src={captured.url} alt={t('common:problemReporter.capturedScreenshotPreview')} className="h-24 w-20 object-cover object-top rounded-lg border border-line shrink-0" />
                <div className="text-xs text-muted">
                  <p className="mb-2">{t('common:problemReporter.screenshotThumbLabel')}</p>
                  <button type="button" onClick={removeScreenshot} disabled={stage === 'submitting'} className="underline text-amber-strong">{t('common:problemReporter.removeScreenshot')}</button>
                </div>
              </div>
            )}

            {error && (
              <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 mb-2 mt-2">{error}</div>
            )}
            </div>

            <div className="flex flex-wrap gap-3 p-4 sm:px-8 border-t border-line bg-card" data-testid="report-confirm-footer">
              <Button variant="secondary" className="flex-auto min-w-fit whitespace-nowrap" onClick={reset} disabled={stage === 'submitting'}>{t('common:actions.cancel')}</Button>
              <Button className="flex-auto min-w-fit whitespace-nowrap" loading={stage === 'submitting'} onClick={handleConfirm}>{t('common:problemReporter.sendReport')}</Button>
            </div>
          </div>
        </div>
      )}

      {sent && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-4 flex justify-center" data-testid="report-sent">
          <div role="status" className={`w-full max-w-md rounded-2xl shadow-2xl border p-4 flex flex-col gap-3 ${sent === 'sent' ? 'bg-card border-line' : 'bg-card border-amber-strong'}`}>
            <p className="text-sm font-semibold text-ink">{sent === 'sent' ? t('common:problemReporter.thanksWeLlTakeALook') : t('common:problemReporter.sentWithoutSomeFiles')}</p>
            <div className="flex flex-wrap gap-3">
              <a href="/messages" className="flex-auto min-w-fit text-center px-4 py-2 rounded-xl bg-amber text-on-amber text-sm font-medium hover:bg-amber-hover">{t('common:problemReporter.openMessages')}</a>
              <Button variant="secondary" size="sm" className="flex-auto min-w-fit" onClick={() => setSent(null)}>{t('common:actions.close')}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
